import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn, todayISO } from '../lib/utils';

// Month grid with big tappable days (same design as bookings.deseno.co.in): month
// + year nav, today ring, selected fill, a Today shortcut. Dates are YYYY-MM-DD
// strings throughout (no timezone shifting). With `rangeEnd` it draws
// selected -> rangeEnd as one band; the parent owns the click logic.
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const ymd = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export function parseYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? { y: +m[1], m: +m[2] - 1, d: +m[3] } : null;
}

/** "30 May 2026" */
export function displayYmd(s) {
  const p = parseYmd(s);
  if (!p) return null;
  return new Date(Date.UTC(p.y, p.m, p.d)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function addMonths({ y, m }, delta) {
  const t = y * 12 + m + delta;
  return { y: Math.floor(t / 12), m: ((t % 12) + 12) % 12 };
}

// Whole weeks (Sunday first) covering the month, with leading/trailing days. Only the
// weeks the month needs (usually 5), so the popover stays short enough for 768px screens.
function monthCells(y, m) {
  const startWd = new Date(Date.UTC(y, m, 1)).getUTCDay();
  const days = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return Array.from({ length: Math.ceil((startWd + days) / 7) * 7 }, (_, i) => {
    const t = new Date(Date.UTC(y, m, i - startWd + 1));
    return { key: ymd(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()), d: t.getUTCDate(), outside: t.getUTCMonth() !== m };
  });
}

const navBtn = 'inline-flex items-center justify-center h-9 w-9 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-black';

export default function MonthCalendar({ selected, rangeEnd, onSelect, onHover }) {
  const today = todayISO();
  const seed = parseYmd(selected) || parseYmd(today);
  const [view, setView] = useState({ y: seed.y, m: seed.m });
  const cells = useMemo(() => monthCells(view.y, view.m), [view.y, view.m]);
  const go = (delta) => setView((v) => addMonths(v, delta));

  return (
    <div className="select-none">
      <div className="flex items-center justify-between pb-2">
        <div className="flex">
          <button type="button" className={navBtn} onClick={() => go(-12)} aria-label="Previous year" title="Previous year"><ChevronsLeft className="h-4 w-4" /></button>
          <button type="button" className={navBtn} onClick={() => go(-1)} aria-label="Previous month" title="Previous month"><ChevronLeft className="h-5 w-5" /></button>
        </div>
        <div className="text-[15px] font-semibold tracking-tight">{MONTHS[view.m]} {view.y}</div>
        <div className="flex">
          <button type="button" className={navBtn} onClick={() => go(1)} aria-label="Next month" title="Next month"><ChevronRight className="h-5 w-5" /></button>
          <button type="button" className={navBtn} onClick={() => go(12)} aria-label="Next year" title="Next year"><ChevronsRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="h-8 flex items-center justify-center text-[11px] font-medium uppercase tracking-wide text-gray-400">{w}</div>
        ))}
      </div>

      {/* No horizontal gap, so in-range days join into one continuous band. */}
      <div className="grid grid-cols-7 gap-y-0.5" onMouseLeave={() => onHover?.(null)}>
        {cells.map((c) => {
          const isSel = c.key === selected || c.key === rangeEnd;
          // YYYY-MM-DD compares correctly as text.
          const inRange = !!selected && !!rangeEnd && c.key > selected && c.key < rangeEnd;
          const isToday = c.key === today;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => onSelect(c.key)}
              onMouseEnter={() => onHover?.(c.key)}
              className={cn(
                'h-10 w-full flex items-center justify-center rounded-lg text-sm tabular-nums transition-colors',
                isSel
                  ? 'bg-black text-white font-semibold hover:bg-black'
                  : inRange
                    ? 'rounded-none bg-gray-100 text-black hover:bg-gray-200'
                    : c.outside
                      ? 'text-gray-300 hover:bg-gray-100'
                      : 'text-gray-900 hover:bg-gray-100',
                isToday && !isSel && 'ring-1 ring-inset ring-black/50 font-semibold',
              )}
              aria-label={displayYmd(c.key)}
              aria-pressed={isSel}
              aria-current={isToday ? 'date' : undefined}
            >
              {c.d}
            </button>
          );
        })}
      </div>

      <div className="flex items-center pt-2 mt-1 border-t">
        <button type="button" onClick={() => onSelect(today)} className="h-8 px-3 rounded-lg text-sm font-medium hover:bg-gray-100">
          Today
        </button>
      </div>
    </div>
  );
}
